import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Box,
    RadioGroup,
    FormControlLabel,
    Radio,
    Typography,
    Button,
    Chip,
    Paper,
    Divider,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    IconButton,
    Collapse,
    Grid,
    Card,
    CardContent,
    Stack,
    Fade,
    Zoom,
    Badge,
    Alert
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocalLibraryIcon from '@mui/icons-material/LocalLibrary';
import ComputerIcon from '@mui/icons-material/Computer';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CloudIcon from '@mui/icons-material/Cloud';
import StarIcon from '@mui/icons-material/Star';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import SchoolIcon from '@mui/icons-material/School';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

const PurchaseOptionSelector = ({ open, onClose, product, onSelect, selectedOption }) => {
    const [selected, setSelected] = useState(selectedOption || 'studyProgramOnly');
    const [expandedBooks, setExpandedBooks] = useState({});

    if (!product || !product.purchaseOptions) {
        return null;
    }

    const handleOptionChange = (event) => {
        setSelected(event.target.value);
    };

    const handleConfirm = () => {
        const option = product.purchaseOptions.find(opt => opt.optionType === selected);
        onSelect(selected, option);
        onClose();
    };

    const toggleBooksExpanded = (optionType) => {
        setExpandedBooks(prev => ({
            ...prev,
            [optionType]: !prev[optionType]
        }));
    };

    const getOptionLabel = (option) => {
        switch (option.optionType) {
            case 'studyProgramOnly':
                return 'Digital Access';
            case 'studyProgramWithBooks':
                return 'Complete Package';
            case 'booksOnly':
                return 'Books Only';
            case 'studyProgramAndRentBooks':
                return 'Study Program with Book Rental';
            default:
                return option.displayName;
        }
    };

    const getOptionDescription = (option) => {
        switch (option.optionType) {
            case 'studyProgramOnly':
                return 'Access to Practice Exams';
            case 'studyProgramWithBooks':
                return 'Everything in Digital + Physical books shipped to you';
            case 'booksOnly':
                return 'Physical books only - No online access';
            case 'studyProgramAndRentBooks':
                return 'Online access plus rental of all required books. Books must be returned after exam.';
            default:
                return '';
        }
    };

    const getOptionIcon = (optionType) => {
        switch (optionType) {
            case 'studyProgramOnly':
                return <CloudIcon sx={{ fontSize: 32 }} />;
            case 'studyProgramWithBooks':
                return <AutoAwesomeIcon sx={{ fontSize: 32 }} />;
            case 'booksOnly':
                return <MenuBookIcon sx={{ fontSize: 32 }} />;
            default:
                return null;
        }
    };

    const getOptionFeatures = (optionType) => {
        switch (optionType) {
            case 'studyProgramOnly':
                return [
                    'Instant online access',
                    'Practice Exams',
                    'Progress tracking',
                    'Mobile compatible'
                ];
            case 'studyProgramWithBooks':
                return [
                    'Everything in Digital Access',
                    'Physical textbooks included',
                    'Keep books forever',
                    'Best value for comprehensive study'
                ];
            case 'booksOnly':
                return [
                    'Physical textbooks only',
                    'Keep books forever',
                    'Perfect for self-study'
                ];
            default:
                return [];
        }
    };

    const getOptionBadge = (optionType) => {
        if (optionType === 'studyProgramWithBooks') {
            return { text: 'MOST POPULAR', color: 'success' };
        }
        return null;
    };

    // Check if product has any books available
    const hasBooks = product.purchaseOptions.some(opt =>
        opt.books && opt.books.length > 0
    );

    const filteredOptions = product.purchaseOptions.filter(option => {
        // Filter out rent books option
        if (option.optionType === 'studyProgramAndRentBooks') return false;

        // If product has no books, only show studyProgramOnly option
        if (!hasBooks) {
            return option.optionType === 'studyProgramOnly';
        }

        return true;
    });

    // Sort options to show in order: studyProgramOnly, booksOnly, studyProgramWithBooks
    const sortedOptions = filteredOptions.sort((a, b) => {
        const order = ['studyProgramOnly', 'booksOnly', 'studyProgramWithBooks'];
        return order.indexOf(a.optionType) - order.indexOf(b.optionType);
    });

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle sx={{ 
                textAlign: 'center', 
                pb: 1,
                borderBottom: '1px solid #e0e0e0'
            }}>
                <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
                    Choose Your Study Package
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {product.name}
                </Typography>
            </DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
                <Grid container spacing={3} sx={{ mb: 3 }}>
                    {sortedOptions.map((option, index) => {
                        const isSelected = selected === option.optionType;
                        const badge = getOptionBadge(option.optionType);
                        const isRecommended = option.optionType === 'studyProgramWithBooks';
                        
                        return (
                        <Grid item xs={12} md={4} key={option.optionType}>
                            <Card 
                                elevation={isSelected ? 8 : 2}
                                onClick={() => setSelected(option.optionType)}
                                sx={{ 
                                    height: '100%',
                                    cursor: 'pointer',
                                    position: 'relative',
                                    border: isSelected ? '2px solid #1976d2' : isRecommended ? '2px solid #4caf50' : '1px solid #e0e0e0',
                                    transition: 'all 0.3s ease',
                                    transform: isSelected ? 'scale(1.02)' : 'scale(1)',
                                    '&:hover': {
                                        transform: 'scale(1.02)',
                                        boxShadow: 6
                                    }
                                }}
                            >
                                {badge && (
                                    <Box sx={{
                                        position: 'absolute',
                                        top: -12,
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        zIndex: 1
                                    }}>
                                        <Chip 
                                            icon={<StarIcon />}
                                            label={badge.text}
                                            color={badge.color}
                                            size="small"
                                            sx={{ fontWeight: 600 }}
                                        />
                                    </Box>
                                )}
                                
                                <CardContent sx={{ p: 2 }}>
                                    <Box sx={{ textAlign: 'center', mb: 2 }}>
                                        <Box sx={{ 
                                            display: 'inline-flex',
                                            p: 1.5,
                                            borderRadius: '50%',
                                            bgcolor: isSelected ? 'primary.light' : 'action.hover',
                                            color: isSelected ? 'primary.main' : 'text.secondary',
                                            mb: 1.5
                                        }}>
                                            {getOptionIcon(option.optionType)}
                                        </Box>
                                        
                                        <Typography variant="h6" component="div" sx={{ fontWeight: 600, mb: 0.5 }}>
                                            {getOptionLabel(option)}
                                        </Typography>
                                        
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontSize: '0.875rem' }}>
                                            {getOptionDescription(option)}
                                        </Typography>
                                        
                                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'baseline', mb: 1.5 }}>
                                            <Typography variant="h4" component="span" sx={{ fontWeight: 700, color: isSelected ? 'primary.main' : 'text.primary' }}>
                                                ${option.price}
                                            </Typography>
                                            {option.isTaxable && (
                                                <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                                                    + tax
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                    
                                    <Divider sx={{ mb: 1.5 }} />
                                    
                                    <Stack spacing={1} sx={{ mb: 1.5 }}>
                                        {getOptionFeatures(option.optionType).map((feature, idx) => (
                                            <Box key={idx} sx={{ display: 'flex', alignItems: 'center' }}>
                                                <CheckCircleIcon 
                                                    sx={{ 
                                                        mr: 1, 
                                                        fontSize: 18,
                                                        color: isSelected ? 'primary.main' : 'success.main'
                                                    }} 
                                                />
                                                <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                                    {feature}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Stack>
                                    
                                    {option.books && option.books.length > 0 && (option.optionType === 'studyProgramWithBooks' || option.optionType === 'booksOnly') && (
                                        <Box sx={{ mt: 1.5 }}>
                                            <Button
                                                fullWidth
                                                variant={expandedBooks[option.optionType] ? "contained" : "outlined"}
                                                size="medium"
                                                startIcon={
                                                    <Badge badgeContent={option.books.length} color="primary">
                                                        <AutoStoriesIcon />
                                                    </Badge>
                                                }
                                                endIcon={expandedBooks[option.optionType] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    toggleBooksExpanded(option.optionType);
                                                }}
                                                sx={{ 
                                                    mb: 1.5,
                                                    borderColor: isSelected ? 'primary.main' : 'divider',
                                                    background: expandedBooks[option.optionType] 
                                                        ? 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)'
                                                        : 'transparent',
                                                    color: expandedBooks[option.optionType] ? 'white' : 'primary.main',
                                                    transition: 'all 0.3s ease',
                                                    '&:hover': {
                                                        transform: 'translateY(-2px)',
                                                        boxShadow: 3
                                                    }
                                                }}
                                            >
                                                {expandedBooks[option.optionType] ? 'Hide' : 'View'} Complete Book List
                                            </Button>
                                            
                                            <Collapse in={expandedBooks[option.optionType]} timeout={500}>
                                                <Fade in={expandedBooks[option.optionType]} timeout={700}>
                                                    <Box>
                                                        <Alert 
                                                            icon={<SchoolIcon />} 
                                                            severity="info" 
                                                            sx={{ 
                                                                mb: 2, 
                                                                bgcolor: 'info.light',
                                                                '& .MuiAlert-icon': { color: 'info.dark' }
                                                            }}
                                                        >
                                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                                {option.books.length} Professional Reference Books Included
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                Physical books shipped directly to your address - yours to keep forever!
                                                            </Typography>
                                                        </Alert>
                                                        
                                                        <Paper 
                                                            elevation={3}
                                                            sx={{ 
                                                                background: 'linear-gradient(to bottom, #ffffff, #f8f9fa)',
                                                                borderRadius: 2,
                                                                overflow: 'hidden',
                                                                border: '1px solid',
                                                                borderColor: 'divider'
                                                            }}
                                                        >
                                                            <Box sx={{ 
                                                                p: 2, 
                                                                bgcolor: 'primary.dark',
                                                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                                            }}>
                                                                <Typography 
                                                                    variant="subtitle1" 
                                                                    sx={{ 
                                                                        color: 'white',
                                                                        fontWeight: 700,
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        gap: 1
                                                                    }}
                                                                >
                                                                    <MenuBookIcon /> 
                                                                    Essential Study Materials
                                                                </Typography>
                                                            </Box>
                                                            
                                                            <List sx={{ 
                                                                maxHeight: 320, 
                                                                overflow: 'auto',
                                                                p: 0,
                                                                '&::-webkit-scrollbar': {
                                                                    width: '8px',
                                                                },
                                                                '&::-webkit-scrollbar-track': {
                                                                    backgroundColor: 'grey.100',
                                                                },
                                                                '&::-webkit-scrollbar-thumb': {
                                                                    backgroundColor: 'primary.main',
                                                                    borderRadius: '4px',
                                                                }
                                                            }}>
                                                                {option.books.map((book, index) => (
                                                                    <Zoom 
                                                                        in={expandedBooks[option.optionType]} 
                                                                        style={{ transitionDelay: `${index * 50}ms` }}
                                                                        key={index}
                                                                    >
                                                                        <ListItem 
                                                                            sx={{ 
                                                                                py: 1.5,
                                                                                px: 2,
                                                                                borderBottom: '1px solid',
                                                                                borderColor: 'divider',
                                                                                transition: 'all 0.2s ease',
                                                                                '&:hover': {
                                                                                    bgcolor: 'action.hover',
                                                                                    pl: 2.5
                                                                                },
                                                                                '&:last-child': {
                                                                                    borderBottom: 'none'
                                                                                }
                                                                            }}
                                                                        >
                                                                            <ListItemIcon sx={{ minWidth: 36 }}>
                                                                                <Box sx={{
                                                                                    width: 28,
                                                                                    height: 28,
                                                                                    borderRadius: '50%',
                                                                                    bgcolor: 'primary.light',
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                    justifyContent: 'center',
                                                                                    fontSize: '0.75rem',
                                                                                    fontWeight: 700,
                                                                                    color: 'primary.dark'
                                                                                }}>
                                                                                    {index + 1}
                                                                                </Box>
                                                                            </ListItemIcon>
                                                                            <ListItemText 
                                                                                primary={
                                                                                    <Typography 
                                                                                        variant="body2" 
                                                                                        sx={{ 
                                                                                            fontWeight: 500,
                                                                                            color: 'text.primary',
                                                                                            lineHeight: 1.6
                                                                                        }}
                                                                                    >
                                                                                        {book.split('–')[0].trim()}
                                                                                    </Typography>
                                                                                }
                                                                                secondary={
                                                                                    book.includes('–') && (
                                                                                        <Typography 
                                                                                            variant="caption" 
                                                                                            sx={{ 
                                                                                                color: 'text.secondary',
                                                                                                fontStyle: 'italic'
                                                                                            }}
                                                                                        >
                                                                                            {book.split('–').slice(1).join('–').trim()}
                                                                                        </Typography>
                                                                                    )
                                                                                }
                                                                            />
                                                                        </ListItem>
                                                                    </Zoom>
                                                                ))}
                                                            </List>
                                                            
                                                            <Box sx={{ 
                                                                p: 2, 
                                                                bgcolor: 'grey.50',
                                                                borderTop: '1px solid',
                                                                borderColor: 'divider'
                                                            }}>
                                                                <Stack direction="row" spacing={1} alignItems="center">
                                                                    <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        All books are the latest editions required for your exam
                                                                    </Typography>
                                                                </Stack>
                                                            </Box>
                                                        </Paper>
                                                    </Box>
                                                </Fade>
                                            </Collapse>
                                        </Box>
                                    )}
                                    
                                    {isSelected && (
                                        <Box sx={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            justifyContent: 'center',
                                            mt: 1.5,
                                            p: 0.75,
                                            bgcolor: 'primary.light',
                                            borderRadius: 1
                                        }}>
                                            <CheckCircleIcon sx={{ mr: 1, color: 'primary.main' }} />
                                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                                Selected
                                            </Typography>
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                    )})}
                </Grid>
                
                <Paper elevation={0} sx={{ 
                    p: 2, 
                    bgcolor: 'grey.50',
                    borderRadius: 2,
                    mb: 2
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                        <LocalShippingIcon color="action" />
                        <Typography variant="body2" color="text.secondary">
                            Tax calculated at checkout for physical books
                        </Typography>
                    </Box>
                </Paper>
                
                <Divider sx={{ mb: 2 }} />
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TrendingUpIcon color="success" sx={{ fontSize: 20 }} />
                        <Typography variant="body2" color="text.secondary">
                            Join thousands of successful exam takers
                        </Typography>
                    </Box>
                    <Box>
                        <Button onClick={onClose} sx={{ mr: 1 }} size="large">
                            Cancel
                        </Button>
                        <Button 
                            variant="contained" 
                            onClick={handleConfirm}
                            size="large"
                            sx={{
                                px: 4,
                                background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
                                boxShadow: '0 3px 5px 2px rgba(33, 150, 243, .3)',
                            }}
                        >
                            Continue with {(() => {
                                const selectedOpt = sortedOptions.find(opt => opt.optionType === selected);
                                if (selectedOpt?.optionType === 'studyProgramOnly') return 'Digital';
                                if (selectedOpt?.optionType === 'booksOnly') return 'Books Only';
                                return 'Complete';
                            })()} Package
                        </Button>
                    </Box>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default PurchaseOptionSelector;
